import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { PrismBadge, PrismIcon } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import type {
  SmartInsightDay,
  SmartInsightDayInsightEntry,
} from 'lumen/models';

import styles from './card-smart-insights.module.css';

export interface MarketSmartInsightsCardSmartInsightsSignature {
  Args: {
    isGroupByStayDate: boolean;
    smartInsightDay: SmartInsightDay;
  };
  Element: HTMLDivElement;
}

export default class MarketSmartInsightsCardSmartInsights extends Component<MarketSmartInsightsCardSmartInsightsSignature> {
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];

  get badgeText(): string {
    const { isGroupByStayDate, smartInsightDay } = this.args;

    if (isGroupByStayDate) {
      return smartInsightDay.hotelName;
    }

    return dayjs(smartInsightDay.stayDate).format('ddd, MMM D');
  }

  get firstInsightEntry(): SmartInsightDayInsightEntry | undefined {
    if (!this.sortedInsightEntries?.length) {
      return undefined;
    }
    return this.sortedInsightEntries[0];
  }

  get isExpanded(): boolean {
    return (
      this.portfolioSmartInsights.selectedSmartInsightDay ===
      this.args.smartInsightDay
    );
  }

  get remainingInsightEntryCount(): number {
    return this.sortedInsightEntries.length - 1;
  }

  get sortedInsightEntries(): SmartInsightDayInsightEntry[] {
    return this.args.smartInsightDay.insightEntriesSortedByRelevance;
  }

  <template>
    <div
      class="{{styles.card}}
        portfolio-smart-insights-card
        {{if this.isExpanded 'expanded'}}"
      ...attributes
    >
      <div class="flex flex-row justify-between gap-200">
        {{#if this.firstInsightEntry}}
          <p class="text-300--regular max-w-[228px]">
            {{htmlSafe this.firstInsightEntry.formattedText}}
          </p>
        {{/if}}

        <PrismIcon
          @color="intelligence"
          @name={{@smartInsightDay.priorityIcon}}
        />
      </div>

      <div
        class="flex max-w-[228px] items-center justify-between gap-400 overflow-hidden"
      >
        <div class={{styles.badge}}>
          <PrismBadge @label={{this.badgeText}} />
        </div>

        {{#if this.remainingInsightEntryCount}}
          <p class="text-200--regular text-neutral-subdued whitespace-nowrap">
            {{t
              "market.smart_insights.count_more"
              count=this.remainingInsightEntryCount
            }}
          </p>
        {{/if}}
      </div>
    </div>
  </template>
}
